import BaseApi, { FetchMethodType } from 'api/BaseApi';
import { CuroApplicationVariables } from 'enums/curo/Variables';
import { VariableValue } from 'product_modules/api/Types';
import { ICuroApplication } from 'types/curo/CuroApplication';


export interface IUpdateCurrentApplicationRequest {
  variables: {
    [K in keyof typeof CuroApplicationVariables as typeof CuroApplicationVariables[K]]?: VariableValue;
  };
}

export interface ISaveIncomeSourcesRequest {
  incomeSources: Array<{
    employmentType: string;
    grossMonthlyIncome: number;
    nextPayDate: string;
    paycheckFrequency: string;
    sourceOfIncome?: string;
  }>;
}

export interface ISaveBankInformationRequest {
  bankAccountType?: string;
  accountNumber: string;
  routingNumber: string;
}

export interface IUploadDocumentsRequest {
  files: File[];
}

export interface ISaveSelectedOfferRequest {
  selectedOfferId: string;
}

export interface ISelectedAccountRequest {
  selectedAccountId: string;
}

export interface IGetPlaidLinkTokenResponse {
  linkToken: string;
}

export interface ISaveAutoPayOptionRequest {
  autoPay: boolean;
}

interface IApplicationProcessApi {
  getCurrentApplication(): Promise<ICuroApplication>;
  updateCurrentApplication(dto: IUpdateCurrentApplicationRequest): Promise<ICuroApplication>;
  saveIncomeSources: (
    applicationId: string,
    incomeSources: ISaveIncomeSourcesRequest,
  ) => Promise<ICuroApplication>;
  saveBankInformation: (
    applicationId: string,
    bankInformation: ISaveBankInformationRequest,
  ) => Promise<ICuroApplication>;
  saveTermsOfUseAcceptance: (applicationId: string) => Promise<ICuroApplication>;
  uploadDocuments: (applicationId: string, documents: IUploadDocumentsRequest) => Promise<void>;
  handleDocumentsUploaded: (applicationId: string) => Promise<ICuroApplication>;
  gpsGatewayCheck: () => Promise<ICuroApplication>;
  wellsFargoCheck: () => Promise<ICuroApplication>;
  saveSelectedOffer: (
    applicationId: string,
    selectedOffer: ISaveSelectedOfferRequest,
    isPPCJourneyLoanOffer?: boolean,
  ) => Promise<ICuroApplication>;
  selectBankAccount: (
    applicationId: string,
    selectedAccount: ISelectedAccountRequest,
  ) => Promise<ICuroApplication>;
  organicCreditAndAutoOriginationCheck: () => Promise<ICuroApplication>;
  refiCreditCheck: () => Promise<ICuroApplication>;
  refiAutoOriginationCheck: () => Promise<ICuroApplication>;
  getPlaidLinkToken: () => Promise<IGetPlaidLinkTokenResponse>;
  plaidSavePublicToken: (publicToken: string) => Promise<ICuroApplication>;
  saveAutoPayOption: (autoPayOption: ISaveAutoPayOptionRequest) => Promise<ICuroApplication>;
  handleOrganicLoanOfferViewed: (applicationId: string) => Promise<ICuroApplication>
  updatePageDisplayedTechnical: (applicationId: string, pageVariableToUpdate: string) => Promise<ICuroApplication>
  runSecondChanceCashflowAndAutoOriginationStrategy: (applicationId: string) => Promise<ICuroApplication>
  runApplicationSummitLead: (applicationId: string) => Promise<ICuroApplication>
}

export default class ApplicationProcessApi extends BaseApi implements IApplicationProcessApi {
  public async runPreliminaryCheck(): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>('/application-process/preliminary-check', {
      method: FetchMethodType.GET,
    });

    return response;
  }

  public async saveAutoPayOption(
    autoPayOption: ISaveAutoPayOptionRequest,
  ): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>('/application-process/auto-pay', {
      method: FetchMethodType.PUT,
      body: autoPayOption,
    });

    return response;
  }

  public async saveTermsOfUseAcceptance(applicationId: string): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/save-terms-of-use-acceptance/${applicationId}`,
      {
        method: FetchMethodType.PUT,
      },
    );

    return response;
  }

  public async saveBankInformation(
    applicationId: string,
    bankInformation: ISaveBankInformationRequest,
  ): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/save-bank-information/${applicationId}`,
      {
        method: FetchMethodType.PUT,
        body: bankInformation,
      },
    );

    return response;
  }

  public async saveIncomeSources(
    applicationId: string,
    incomeSources: ISaveIncomeSourcesRequest,
  ): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/save-income-sources/${applicationId}`,
      {
        method: FetchMethodType.PUT,
        body: incomeSources,
      },
    );

    return response;
  }

  public async getCurrentApplication(): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>('/application-process/current-application');

    return response;
  }

  public async updateCurrentApplication(
    dto: IUpdateCurrentApplicationRequest,
  ): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      '/application-process/current-application',
      {
        method: FetchMethodType.PUT,
        body: dto,
      },
    );

    return response;
  }

  public async uploadDocuments(
    applicationId: string,
    { files }: IUploadDocumentsRequest,
  ): Promise<void> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });

    await this.fetch<{}>(`/application-process/upload-documents/${applicationId}`, {
      method: FetchMethodType.POST,
      body: formData,
      resetDefaultHeaders: true,
    });
  }

  public async handleDocumentsUploaded(applicationId: string): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/handle-documents-uploaded/${applicationId}`,
      {
        method: FetchMethodType.PUT,
      },
    );

    return response;
  }

  public async gpsGatewayCheck(): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>('/application-process/gps-gateway-check');

    return response;
  }

  public async wellsFargoCheck(): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>('/application-process/wells-fargo-check');

    return response;
  }

  public async saveSelectedOffer(
    applicationId: string,
    selectedOffer: ISaveSelectedOfferRequest,
    isPPCJourneyLoanOffer?: boolean,
  ): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/save-selected-offer/${applicationId}`,
      {
        method: FetchMethodType.PUT,
        body: {
          selectedOffer,
          isPPCJourneyLoanOffer,
        },
      },
    );

    return response;
  }

  public async selectBankAccount(
    applicationId: string,
    selectedAccount: ISelectedAccountRequest,
  ): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/select-bank-account/${applicationId}`,
      {
        method: FetchMethodType.PUT,
        body: selectedAccount,
      },
    );

    return response;
  }

  public async organicCreditAndAutoOriginationCheck(): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      '/application-process/organic-credit-and-auto-origination-check',
    );

    return response;
  }

  public async refiCreditCheck(): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>('/application-process/refi-credit-check');

    return response;
  }

  public async refiAutoOriginationCheck(): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      '/application-process/refi-auto-origination-check',
    );

    return response;
  }

  public async getPlaidLinkToken(): Promise<IGetPlaidLinkTokenResponse> {
    const response = await this.fetch<IGetPlaidLinkTokenResponse>(
      '/application-process/plaid-link-token',
    );

    return response;
  }

  public async plaidSavePublicToken(publicToken: string): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      '/application-process/plaid-save-public-token',
      {
        method: FetchMethodType.POST,
        body: {
          publicToken,
        },
      },
    );

    return response;
  }

  public async runSnowflakeRefiScoreCheck() {
    const response = await this.fetch<ICuroApplication>(
      '/application-process/snowflake-refi-score-check',
    );

    return response;
  }

  public async runSnowflakeRefiReloanCheck() {
    const response = await this.fetch<ICuroApplication>(
      '/application-process/snowflake-refi-reloan-check',
    );

    return response;
  }

  public async handleOrganicLoanOfferViewed(applicationId: string): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/handle-organic-loan-offer-viewed/${applicationId}`,
      {
        method: FetchMethodType.PUT,
      },
    );

    return response;
  }

  public async updatePageDisplayedTechnical(
    applicationId: string,
    pageVariableToUpdate: string,
  ): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/handle-update-page-displayed-technical/${applicationId}`,
      {
        method: FetchMethodType.PUT,
        body: {
          pageVariableToUpdate,
        },
      },
    );

    return response;
  }

  public async runSecondChanceCashflowAndAutoOriginationStrategy(
    applicationId: string,
  ): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/second-chance-cashflow-and-auto-origination/${applicationId}`,
      {
        method: FetchMethodType.POST,
      },
    );

    return response;
  }

  public async runApplicationSummitLead(applicationId: string): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/run-summit-lead-integration-strategy/${applicationId}`,
      {
        method: FetchMethodType.POST,
      },
    );

    return response;
  }

  public async handleDeclineApplication(): Promise<ICuroApplication> {
    const response = await this.fetch<ICuroApplication>(
      `/application-process/handle-decline-application`,
      {
        method: FetchMethodType.PUT,
      },
    );

    return response;
  }
}
